
import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import "./EvaDocumentDrawer.css";


//========================================================
// EVA Document Library
//========================================================

const DOCUMENTS = [

    {
        id: "doc1",
        title: "Health & Safety Policy",
        description:
            "Workplace health, safety responsibilities and policy guidance.",
        category: "Health & Safety",
        type: "Policy Document",
        filename: "document1.pdf",
        icon: "shield"
    },

    {
        id: "doc2",
        title: "Fire Risk Assessment",
        description:
            "Fire safety assessment, precautions and emergency arrangements.",
        category: "Compliance",
        type: "Risk Assessment",
        filename: "document2.pdf",
        icon: "fire"
    },

    {
        id: "doc3",
        title: "Plant Maintenance Manual",
        description:
            "Equipment operation, maintenance procedures and servicing guidance.",
        category: "Operations",
        type: "O&M Manual",
        filename: "document3.pdf",
        icon: "settings"
    },

    {
        id: "doc4",
        title: "Building Floor Plans",
        description:
            "Building layouts, room arrangements and floor drawings.",
        category: "Drawings",
        type: "Building Drawings",
        filename: "document4.pdf",
        icon: "drawing"
    },

    {
        id: "doc5",
        title: "Emergency Procedures",
        description:
            "Evacuation procedures, incident response and emergency contacts.",
        category: "Health & Safety",
        type: "Emergency Plan",
        filename: "document5.pdf",
        icon: "alert"
    }

];


//========================================================
// Document Icon
//========================================================

function DocumentIcon({ type }) {

    const icons = {
        shield: "✚",
        fire: "♨",
        settings: "⚙",
        drawing: "▤",
        alert: "!"
    };

    return (

        <div className="eva-doc-icon">

            <span>
                {icons[type] || "▤"}
            </span>

        </div>

    );

}


//========================================================
// Main Component
//========================================================

export default function EvaDocumentDrawer({ onClose }) {

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All");
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const [missingFiles, setMissingFiles] = useState({});

    //----------------------------------------------------
    // Document URL
    //----------------------------------------------------

    const getDocumentUrl = filename => {

        const base = (process.env.PUBLIC_URL || "")
            .replace(/\/$/, "");

        return `${base}/eva/docs/${filename}`;

    };

    //----------------------------------------------------
    // Categories
    //----------------------------------------------------

    const categories = useMemo(() => {

        return [
            "All",
            ...new Set(DOCUMENTS.map(doc => doc.category))
        ];

    }, []);

    //----------------------------------------------------
    // Filtered Documents
    //----------------------------------------------------

    const filteredDocuments = useMemo(() => {

        const term = search.trim().toLowerCase();

        return DOCUMENTS.filter(doc => {

            const categoryMatches =
                category === "All" ||
                doc.category === category;

            const searchMatches =
                !term ||
                [
                    doc.title,
                    doc.description,
                    doc.category,
                    doc.type,
                    doc.filename
                ].some(value =>
                    value.toLowerCase().includes(term)
                );

            return categoryMatches && searchMatches;

        });

    }, [search, category]);

    //----------------------------------------------------
    // Check for Missing PDF
    //----------------------------------------------------

    useEffect(() => {

        let cancelled = false;

        const checkDocuments = async () => {

            const results = {};

            await Promise.all(

                DOCUMENTS.map(async doc => {

                    try {

                        const response = await fetch(
                            getDocumentUrl(doc.filename),
                            {
                                method: "GET",
                                headers: {
                                    Range: "bytes=0-4"
                                }
                            }
                        );

                        if (!response.ok) {

                            results[doc.id] = true;
                            return;

                        }

                        const contentType =
                            response.headers.get("content-type") || "";

                        if (!contentType.toLowerCase().includes("pdf")) {

                            const content = await response.text();

                            results[doc.id] =
                                !content.startsWith("%PDF-");

                        } else {

                            results[doc.id] = false;

                        }

                    }
                    catch (error) {

                        results[doc.id] = true;

                    }

                })

            );

            if (!cancelled) {
                setMissingFiles(results);
            }

        };

        checkDocuments();

        return () => {
            cancelled = true;
        };

    }, []);

    //----------------------------------------------------
    // Escape Key
    //----------------------------------------------------

    useEffect(() => {

        const handleKeyDown = event => {

            if (event.key !== "Escape") {
                return;
            }

            if (viewerOpen) {

                if (fullScreen) {
                    setFullScreen(false);
                } else {
                    setViewerOpen(false);
                }

            } else {

                onClose?.();

            }

        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };

    }, [viewerOpen, fullScreen, onClose]);

    //----------------------------------------------------
    // Open Document
    //----------------------------------------------------

    const openDocument = doc => {

        setSelectedDocument(doc);
        setViewerOpen(true);
        setFullScreen(false);

    };

    //----------------------------------------------------
    // Close Viewer
    //----------------------------------------------------

    const closeViewer = () => {

        setViewerOpen(false);
        setFullScreen(false);

    };

    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <div className="eva-doc-root">

            {/*===============================================
              Background
            ===============================================*/}

            <div
                className="eva-doc-backdrop"
                onClick={onClose}
            />

            {/*===============================================
              Right Drawer
            ===============================================*/}

            <aside className="eva-doc-drawer">

                {/*===========================================
                  Header
                ===========================================*/}

                <header className="eva-doc-header">

                    <div>

                        <small>OMNISCAN PRO / EVA</small>

                        <strong>
                            BUILDING DOCUMENTATION
                        </strong>

                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close documentation"
                    >
                        ×
                    </button>

                </header>

                {/*===========================================
                  Drawer Content
                ===========================================*/}

                <div className="eva-doc-content">

                    <div className="eva-doc-intro">

                        <div className="eva-doc-eyebrow">
                            CONNECTED BUILDING INFORMATION
                        </div>

                        <h2>
                            Document Library
                        </h2>

                        <p>
                            Browse building records, drawings,
                            maintenance manuals and safety
                            documentation.
                        </p>

                    </div>

                    {/*=======================================
                      Search
                    =======================================*/}

                    <div className="eva-doc-search">

                        <span>⌕</span>

                        <input
                            type="text"
                            value={search}
                            placeholder="Search documents..."
                            onChange={event =>
                                setSearch(event.target.value)
                            }
                        />

                        {search && (

                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                aria-label="Clear search"
                            >
                                ×
                            </button>

                        )}

                    </div>

                    {/*=======================================
                      Category Filters
                    =======================================*/}

                    <div className="eva-doc-categories">

                        {categories.map(item => (

                            <button
                                key={item}
                                type="button"
                                className={
                                    "eva-doc-category " +
                                    (category === item
                                        ? "active"
                                        : "")
                                }
                                onClick={() => setCategory(item)}
                            >
                                {item}
                            </button>

                        ))}

                    </div>

                    {/*=======================================
                      Results Header
                    =======================================*/}

                    <div className="eva-doc-list-heading">

                        <strong>
                            DOCUMENT LIBRARY
                        </strong>

                        <span>
                            {filteredDocuments.length}
                            {" "}
                            document
                            {filteredDocuments.length !== 1 ? "s" : ""}
                        </span>

                    </div>

                    {/*=======================================
                      Document List
                    =======================================*/}

                    <div className="eva-doc-list">

                        {filteredDocuments.length === 0 && (

                            <div className="eva-doc-empty">

                                No documents match your search.

                            </div>

                        )}

                        {filteredDocuments.map(doc => (

                            <button
                                key={doc.id}
                                type="button"
                                className="eva-doc-item"
                                onClick={() => openDocument(doc)}
                            >

                                <DocumentIcon type={doc.icon} />

                                <div className="eva-doc-item-info">

                                    <strong>
                                        {doc.title}
                                    </strong>

                                    <span>
                                        {doc.type}
                                        {" · "}
                                        PDF
                                    </span>

                                    <small>
                                        {doc.description}
                                    </small>

                                    {missingFiles[doc.id] && (

                                        <em>
                                            PDF not yet added
                                        </em>

                                    )}

                                </div>

                                <span className="eva-doc-item-arrow">
                                    ›
                                </span>

                            </button>

                        ))}

                    </div>

                </div>

                {/*===========================================
                  Drawer Footer
                ===========================================*/}

                <footer className="eva-doc-footer">

                    <span>
                        <span className="eva-doc-footer-dot" />
                        OMNISCAN DOCUMENT LIBRARY
                    </span>

                    <small>
                        Demonstration collection
                    </small>

                </footer>

            </aside>

            {/*===============================================
              PDF Viewer
            ===============================================*/}

            {viewerOpen && selectedDocument && (

                <div
                    className={
                        "eva-doc-viewer " +
                        (fullScreen ? "fullscreen" : "")
                    }
                    role="dialog"
                    aria-label={selectedDocument.title}
                >

                    {/*=======================================
                      Viewer Header
                    =======================================*/}

                    <div className="eva-doc-viewer-header">

                        <div className="eva-doc-viewer-title">

                            <small>
                                OMNISCAN / DOCUMENT VIEWER
                            </small>

                            <strong>
                                {selectedDocument.title}
                            </strong>

                        </div>

                        <div className="eva-doc-viewer-actions">

                            <a
                                href={getDocumentUrl(
                                    selectedDocument.filename
                                )}
                                download={selectedDocument.filename}
                                title="Download PDF"
                            >
                                ↓
                            </a>

                            <button
                                type="button"
                                onClick={() =>
                                    setFullScreen(value => !value)
                                }
                                title={
                                    fullScreen
                                        ? "Restore viewer"
                                        : "Expand viewer"
                                }
                            >
                                {fullScreen ? "⊟" : "□"}
                            </button>

                            <button
                                type="button"
                                onClick={closeViewer}
                                title="Close viewer"
                            >
                                ×
                            </button>

                        </div>

                    </div>

                    {/*=======================================
                      PDF Frame
                    =======================================*/}

                    <div className="eva-doc-viewer-body">

                        {missingFiles[selectedDocument.id] ? (

                            <div className="eva-doc-missing">

                                <div className="eva-doc-missing-icon">
                                    ▤
                                </div>

                                <h3>
                                    Document Not Yet Available
                                </h3>

                                <p>
                                    Add the PDF to the following
                                    location to enable its preview:
                                </p>

                                <code>
                                    public/eva/docs/
                                    {selectedDocument.filename}
                                </code>

                            </div>

                        ) : (

                            <iframe
                                key={selectedDocument.id}
                                title={selectedDocument.title}
                                src={
                                    getDocumentUrl(
                                        selectedDocument.filename
                                    ) + "#toolbar=1&navpanes=0"
                                }
                            />

                        )}

                    </div>

                    {/*=======================================
                      Viewer Footer
                    =======================================*/}

                    <div className="eva-doc-viewer-footer">

                        <span>
                            {selectedDocument.type}
                        </span>

                        <span>
                            {selectedDocument.filename}
                        </span>

                    </div>

                </div>

            )}

        </div>

    );

}
